import { Box, Button, Tab, Tabs, TextField } from "@mui/material";
import Breadcrumb from "@/components/Breadcrumb";
import { useEffect, useMemo, useState } from "react";
import {
  useGetAllMaterialTypes,
  useGetSpecsForOpenRows,
} from "@/hooks/materialTypeHook";
import type { MaterialType, MaterialTypeSpec } from "@/api/materialTypeService";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import MaterialTypeManagementTable from "./MaterialTypeManagementTable";
import { DefaultSecondaryButton } from "@/styles/globalstyles";
import Add from "@mui/icons-material/Add";
import SaveMaterialTypeModal from "./SaveMaterialTypeModal";
import SaveSpecModal from "./SaveSpecModal";
import { usePermissions } from "@/hooks/usePermissions";

const MaterialTypeManagement = () => {
  const canEdit = usePermissions().canEdit("master.material");
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [tabValue, setTabValue] = useState<number>(0);
  const [openRows, setOpenRows] = useState<{ [key: number]: boolean }>({});
  const [openSaveModal, setOpenSaveModal] = useState<boolean>(false);
  const [openSaveSpecModal, setOpenSaveSpecModal] = useState<boolean>(false);
  const [selectedMaterialType, setSelectedMaterialType] =
    useState<MaterialType | null>(null);
  const [selectedSpec, setSelectedSpec] = useState<MaterialTypeSpec>(
    {} as MaterialTypeSpec
  );

  const openMaterialTypeIds = useMemo(() => {
    return Object.keys(openRows)
      .filter((key) => openRows[parseInt(key)])
      .map(Number);
  }, [openRows]);

  const {
    data: matTypeData,
    isLoading: matTypeIsLoading,
    isError: matTypeIsError,
  } = useGetAllMaterialTypes({ active_only: tabValue === 0 });

  const {
    data: specsData,
    isLoading: specsIsLoading,
    isError: specsIsError,
  } = useGetSpecsForOpenRows(openMaterialTypeIds, { active_only: false });

  useQueryHandler({
    isError: matTypeIsError || specsIsError,
    errorMessage: "เกิดข้อผิดพลาดในการโหลดข้อมูล",
  });

  useEffect(() => {
    document.title = "Material Type Management | AMPA";
  }, []);

  const filteredMaterialTypes = useMemo(() => {
    if (!matTypeData) return [];

    let filtered = matTypeData;

    if (searchFilter) {
      const lowerSearch = searchFilter.toLowerCase();
      filtered = filtered.filter((item: MaterialType) =>
        item.material_type_name.toLowerCase().includes(lowerSearch)
      );
    }

    return filtered;
  }, [matTypeData, searchFilter]);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddMaterialType = () => {
    setOpenSaveModal(true);
    setSelectedMaterialType(null);
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb current="master.material" />
      <Box mt={1}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <TextField
              label="ค้นหา Material Type"
              variant="outlined"
              size="small"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              sx={{
                mt: 2,
                mb: 1,
                width: "30%",
                "& .MuiInputLabel-root": {
                  fontSize: "0.8rem",
                  color: "#666666",
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#cccccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#999999",
                  },
                },
              }}
            />
          </Box>
          <Box gap={1} display="flex">
            <Button
              variant="outlined"
              sx={{
                ...DefaultSecondaryButton,
                mt: 2,
                mb: 1,
                alignItems: "center",
              }}
              onClick={() => handleAddMaterialType()}
              disabled={!canEdit}
            >
              <Add /> เพิ่ม Material Type
            </Button>
          </Box>
        </Box>
        <Box>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={tabValue} onChange={handleChangeTab}>
              <Tab label="Active" />
              <Tab label="Inactive" />
            </Tabs>
          </Box>
          <Box sx={{ p: 1, borderRadius: 1 }}>
            <MaterialTypeManagementTable
              data={filteredMaterialTypes || []}
              setOpenRows={setOpenRows}
              openRows={openRows}
              specsData={specsData || {}}
              isLoadingData={matTypeIsLoading}
              isLoadingSpecs={specsIsLoading}
              handleEdit={(materialType: MaterialType) => () => {
                setSelectedMaterialType(materialType);
                setOpenSaveModal(true);
              }}
              handleAddSpec={(
                materialTypeId: number,
                materialTypeName: string
              ) => {
                setSelectedSpec({
                  material_type_id: materialTypeId,
                  material_type_name: materialTypeName,
                } as MaterialTypeSpec);
                setOpenSaveSpecModal(true);
              }}
              handleEditSpec={(spec: MaterialTypeSpec) => {
                setSelectedSpec(spec);
                setOpenSaveSpecModal(true);
              }}
            />
          </Box>
        </Box>
      </Box>

      <SaveMaterialTypeModal
        open={openSaveModal}
        handleClose={() => {
          setOpenSaveModal(false);
          setSelectedMaterialType(null);
        }}
        selectedMaterialType={selectedMaterialType}
      />

      <SaveSpecModal
        open={openSaveSpecModal}
        handleClose={() => {
          setOpenSaveSpecModal(false);
        }}
        selectedSpec={selectedSpec}
      />
    </Box>
  );
};

export default MaterialTypeManagement;
